import React, { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from '~/components/ErrorFallback';
import Layout from '~/components/Layout';
import InternalHeader from '~/components/Layout/components/InternalHeader';
import InternalContent from '~/components/Layout/components/InternalContent';
import { useLang } from '~/hooks/useLang';
import SlideShow from '~/components/SlideShow';
import CountQualifiers from '~/features/Operation/components/CountQualifiers';
import DispatchByTypeOperationPieChart from '~/features/Operation/components/DispatchByTypeOperationPieChart';
import DispatchBySubTypeOperationPieChart from '~/features/Operation/components/DispatchBySubTypeOperationPieChart';
import DispatchByClosedStatusPieChart from '~/features/Operation/components/DispatchByClosedStatusPieChart';
import DispatchByDistrictPieChart from '~/features/Operation/components/DispatchByDistrictPieChart';
import DispatchByEntryOriginPieChart from '~/features/Operation/components/DispatchByEntryOriginPieChart';
import DispatchByDeviceBarChart from '~/features/Operation/components/DispatchByDeviceBarChart';
import DispatchByCategoryOperationPieChart from '~/features/Operation/components/DispatchByCategoryOperationPieChart';
import { Button, Grid } from '@mui/material';
import { useHookstate } from '@hookstate/core';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { LoadingButton } from '@mui/lab';
import FilterAutocompleteOperation from '~/components/FilterAutocompleteOperation';
import DispatchByConnectedDevicesXStatusPieChart from '../components/DispatchByConnectedDevicesXStatusPieChart';

interface filtersProps {
  readonly operation: number | null,
  readonly initialDate: string | null,
}

const filtersDefault = {
  operation: null,
  initialDate: null
}


export default function Dashboard() {
  const { translate } = useLang();
  const loading = useHookstate<boolean>(false)
  // const operations = useHookstate<readonly IOperation[]>([]);
  // const operationSelect = useHookstate<string | null>(null);
  const operationId = useHookstate<number | null>(null)
  const [initialDate, setInitialDate] = React.useState<Dayjs | null>(null);
  const refresh = useHookstate<boolean>(false);
  const filters = useHookstate<filtersProps>(filtersDefault);

  const CountQualifiersRefresh = useHookstate<boolean>(false);
  const DispatchByTypeOperationPieChartRefresh = useHookstate<boolean>(false);
  const DispatchBySubTypeOperationPieChartRefresh = useHookstate<boolean>(false);
  const DispatchByClosedStatusPieChartRefresh = useHookstate<boolean>(false);
  const DispatchByDeviceBarChartRefresh = useHookstate<boolean>(false);
  const DispatchByCategoryOperationPieChartRefresh = useHookstate<boolean>(false);
  const DispatchByDistrictPieChartRefresh = useHookstate<boolean>(false);
  const DispatchByEntryOriginRefresh = useHookstate<boolean>(false);
  const DispatchByConnectedDevicesXStatusRefresh = useHookstate<boolean>(false);

  useEffect(() => {
    document.title = translate('View Operations - Dispatch System')
  }, [])

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Layout marginLayout={true}>
        <InternalHeader title={translate('Operation')} />

        <InternalContent title={translate('Filters')} expandButton={true} sxFont={{ fontWeight: 'bold', fontSize: 18 }}>
          <Grid container direction={'row'}>
            <Grid item xs={2} paddingTop={1}>
              <FilterAutocompleteOperation
                isOperation={true}
                onOperationChange={(operation) => {
                  operationId.set(operation)
                }}
                onOperationStartDate={(startDate) => {
                  setInitialDate(dayjs(startDate))
                }}
                operationId={operationId.get()}
              />
            </Grid>
            <Grid item xs={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'pt-br'}>
                <DateTimePicker label={translate(`Initial Date`) + '*'}
                  format="DD/MM/YYYY HH:mm"
                  ampm={false}
                  ampmInClock={false}
                  value={initialDate}
                  sx={{ width: '100%' }}
                  onChange={(newValue: Dayjs | null) => setInitialDate(newValue ?? null)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { margin: 1 },
                      inputProps: {
                        placeholder: translate(`dd/mm/yyyy`)
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <Grid container sx={{ display: 'flex', justifyContent: 'end', margin: 0, padding: 0, gap: 2 }} >
            <Button
              variant='outlined'
              onClick={() => {
                setInitialDate(null)
                operationId.set(null)
            }}>
              {translate('Clear Filters')}
            </Button>
            <LoadingButton
              loading={loading.get()}
              variant="contained"
              disabled={!operationId.get() || !initialDate}
              onClick={() => {
                filters.set({
                  operation: operationId.get() ? operationId.get() : null,
                  initialDate: initialDate ? dayjs(initialDate).format() : null,
                })
                refresh.set(!refresh.get())
              }}
            >
              {translate('Start')}
            </LoadingButton>
          </Grid>
        </InternalContent>

        {filters.operation.get() && filters.initialDate.get() ? (
          <InternalContent expandButton={false} sxFont={{ fontWeight: 'bold', fontSize: 18 }}>
            <Grid container xs={12}>
              <SlideShow componentsRender={[
                {
                  component: <CountQualifiers operationId={filters.operation.get()} initialDate={filters.initialDate.get()} endDate={null} refresh={refresh.get()} loading={loading} />,
                  refreshFunction: () => CountQualifiersRefresh.set(!CountQualifiersRefresh.get())
                },
                {
                  component: <DispatchByTypeOperationPieChart operationId={filters.operation.get()} initialDate={filters.initialDate.get()} endDate={null} refresh={refresh.get()} loading={loading} />,
                  refreshFunction: () => DispatchByTypeOperationPieChartRefresh.set(!DispatchByTypeOperationPieChartRefresh.get())
                },
                {
                  component: <DispatchBySubTypeOperationPieChart operationId={filters.operation.get()} initialDate={filters.initialDate.get()} endDate={null} refresh={refresh.get()} loading={loading} />,
                  refreshFunction: () => DispatchBySubTypeOperationPieChartRefresh.set(!DispatchBySubTypeOperationPieChartRefresh.get())
                },
                {
                  component: <DispatchByClosedStatusPieChart operationId={filters.operation.get()} initialDate={filters.initialDate.get()} endDate={null} refresh={refresh.get()} loading={loading} />,
                  refreshFunction: () => DispatchByClosedStatusPieChartRefresh.set(!DispatchByClosedStatusPieChartRefresh.get())
                },
                {
                  component: <DispatchByDeviceBarChart operationId={filters.operation.get()} initialDate={filters.initialDate.get()} endDate={null} refresh={refresh.get()} loading={loading} />,
                  refreshFunction: () => DispatchByDeviceBarChartRefresh.set(!DispatchByDeviceBarChartRefresh.get())
                },
                {
                  component: <DispatchByCategoryOperationPieChart operationId={filters.operation.get()} initialDate={filters.initialDate.get()} endDate={null} refresh={refresh.get()} loading={loading} />,
                  refreshFunction: () => DispatchByCategoryOperationPieChartRefresh.set(!DispatchByCategoryOperationPieChartRefresh.get())
                },
                {
                  component: <DispatchByDistrictPieChart operationId={filters.operation.get()} initialDate={filters.initialDate.get()} endDate={null} refresh={refresh.get()} loading={loading} />,
                  refreshFunction: () => DispatchByDistrictPieChartRefresh.set(!DispatchByDistrictPieChartRefresh.get())
                },
                {
                  component: <DispatchByEntryOriginPieChart operationId={filters.operation.get()} initialDate={filters.initialDate.get()} endDate={null} refresh={refresh.get()} loading={loading} />,
                  refreshFunction: () => DispatchByEntryOriginRefresh.set(!DispatchByEntryOriginRefresh.get())
                },
                {
                  component: <DispatchByConnectedDevicesXStatusPieChart operationId={filters.operation.get()} initialDate={filters.initialDate.get()} endDate={null} refresh={refresh.get()} loading={loading} />,
                  refreshFunction: () => DispatchByConnectedDevicesXStatusRefresh.set(!DispatchByConnectedDevicesXStatusRefresh.get())
                }
              ]} />
            </Grid>
          </InternalContent>
        ) : false}
      </Layout>
    </ErrorBoundary>
  )
}
